import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PersonIcon from '@mui/icons-material/Person';
import { Card, CardContent, Chip, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { Link } from 'react-router';

import { url } from '../../../appRouter/url';
import { MarkedAsDeletedAlert } from '../../../components/MarkedAsDeletedAlert';
import { anbieter, type SapStatus, sapStatus, type VeranstaltungFromSearch } from '../../../dtos';

type SearchResultCardProps = {
  readonly veranstaltung: VeranstaltungFromSearch;
};

const formatDateRange = (ablauf: VeranstaltungFromSearch['ablauf']): string => {
  if (ablauf.length === 0) {
    return '';
  }

  const sortedAblauf = [...ablauf].sort((a, b) => {
    const aStart = new Date(a.start);
    const bEnde = new Date(b.ende);
    return aStart.getTime() - bEnde.getTime();
  });

  const start = new Date(sortedAblauf[0].start);
  const ende = new Date(sortedAblauf[sortedAblauf.length - 1].ende);
  const startString = start.toLocaleDateString('de-DE');
  const endeString = ende.toLocaleDateString('de-DE');
  return `${startString} - ${endeString}`;
};

const badgeColor = (status: SapStatus | undefined): 'error' | 'info' | 'success' => {
  switch (status) {
    case sapStatus.ABGESAGT: {
      return 'error';
    }

    case sapStatus.GEPLANT: {
      return 'info';
    }

    default:
      return 'success';
  }
};

export const SearchResultCard: React.FC<SearchResultCardProps> = ({ veranstaltung }: SearchResultCardProps) => (
  <Tooltip
    title={
      veranstaltung.geloescht
        ? `Veranstaltung ist als "Zu Löschen" markiert. Grund: ${veranstaltung.geloeschtWeil}`
        : ''
    }
  >
    <Card sx={{ minWidth: '100%', marginBottom: 2, opacity: veranstaltung.geloescht ? 0.5 : 1 }}>
      <CardContent>
        <MarkedAsDeletedAlert geloeschtWeil={veranstaltung.geloeschtWeil} />
        <Grid container spacing={2}>
          <Grid size={10}>
            <Link to={url.veranstaltungen(veranstaltung.sapId)} style={{ textDecoration: 'none' }}>
              <Typography sx={{ fontSize: 20, ':hover': 'rgba(0, 0, 0, 0.5);' }}>
                {veranstaltung.title} <Typography variant="headingXxs"> {veranstaltung.akaVeranstaltungId}</Typography>
              </Typography>
            </Link>
            <br />
            <Typography variant="paragraph" color="text.secondary">
              {' '}
              {veranstaltung.ortBezeichnung ?? veranstaltung.ortKuerzel}
            </Typography>
            <Grid container sx={{ marginTop: 1 }}>
              <Grid size={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <AccessTimeIcon fontSize="small" />
                  <Typography variant="paragraphSm">{formatDateRange(veranstaltung.ablauf)}</Typography>
                </Stack>
              </Grid>
              <Grid size={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <PersonIcon fontSize="small" />
                  <Typography variant="paragraphSm">{veranstaltung.experten?.join(', ') ?? ' - '}</Typography>
                </Stack>
              </Grid>
              <Grid size={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <ApartmentIcon fontSize="small" />
                  <Typography variant="paragraphSm">{veranstaltung.geschaeftsbereich}</Typography>
                </Stack>
              </Grid>
            </Grid>
            <Stack direction="row" alignItems="center" gap={1} sx={{ marginTop: 1 }}>
              <Chip size="small" label={veranstaltung.status} color={badgeColor(veranstaltung.status)} />
              {veranstaltung.anbieter && (
                <Chip size="small" label={anbieter[veranstaltung.anbieter]} sx={{ bgcolor: '#1ed75f' }} />
              )}
              <Chip size="small" label={veranstaltung.klassifizierung} color="default" />
            </Stack>
          </Grid>
          <Grid size={2}>
            <Stack direction="column" alignItems="end">
              <Typography variant="paragraphSm">Maximale Teilnehmer</Typography>
              <Typography variant="headingXxs">{veranstaltung.capacitymax}</Typography>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  </Tooltip>
);
